import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	Input,
	OnInit,
	Output,
} from '@angular/core';
import {
	DepositHolder,
	DepositHolderSelections,
	MatterFieldMappings,
	MatterPropertyType,
	PropertyStatus,
} from '@models/matter';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import { MatterDispatch } from '@state/matter';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { BehaviorSubject, Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import {
	DropdownResult,
	SelectionGroup,
} from 'src/app/shared/atomic/atoms/dropdown-options/dropdown-options.component';
import { TestHooks } from './deposit-holder-dropdown.hooks';
import { AI_GENERATED_ICON } from '@core/util/icon';

export enum DepositHolderDropdownMode {
	SELF_UPDATING = 'selfUpdating',
	PASSTHROUGH_SELECTION = 'passthroughSelection',
}

@UntilDestroy()
@Component({
	selector: 'app-deposit-holder-dropdown',
	templateUrl: './deposit-holder-dropdown.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class DepositHolderDropdownComponent implements OnInit {
	testHooks = TestHooks;
	public AI_ICON = AI_GENERATED_ICON.icon;
	public AI_TOOLTIP = AI_GENERATED_ICON.tooltip;
	public AI_CLASSES = AI_GENERATED_ICON.classes;
	public depositHolderSelections: SelectionGroup<{
		name: string;
		value: DepositHolder;
	}>[];
	public depositHolderName$ = new BehaviorSubject('');
	public depositHolder: string;

	@Input() orientation: 'row' | 'col' = 'row';
	@Input() skeletonLoading = false;
	@Input() mode: DepositHolderDropdownMode =
		DepositHolderDropdownMode.SELF_UPDATING;

	@Output() onDepositHolderSelect: EventEmitter<DepositHolder> =
		new EventEmitter();

	@SelectSnapshot(SelectedMatter.id) matterId: string;
	@Select(SelectedMatter.getSelectedDepositHolder)
	depositHolder$: Observable<DepositHolder>;
	@Select(SelectedMatter.propertyType)
	propertyType$: Observable<MatterPropertyType>;
	@Select(SelectedMatter.getFieldMappings)
	fieldMappings$: Observable<MatterFieldMappings>;

	constructor(private matterDispatch: MatterDispatch) {}

	ngOnInit(): void {
		this.propertyType$.pipe(untilDestroyed(this)).subscribe((propertyType) => {
			// if property status is different from new, builder should not be selectable as a deposit holder
			if (
				propertyType &&
				propertyType?.status !== PropertyStatus.NEW &&
				this.depositHolder === DepositHolder.BUILDER
			) {
				this.matterDispatch.SetDepositHolder(this.matterId, null);
			}
			this.depositHolderSelections = this.getDepositHolderSelections(
				propertyType?.status,
			);
		});

		this.depositHolder$
			.pipe(untilDestroyed(this))
			.subscribe((depositHolder) => {
				if (!depositHolder) {
					this.matterDispatch.SetDepositHolder(
						this.matterId,
						DepositHolder.VENDOR_BROKERAGE,
					);
					return;
				}

				const depositHolderName = this.depositHolderSelections
					?.at(0)
					?.items.find((selection) => selection.value === depositHolder)?.name;

				if (depositHolderName) {
					this.depositHolderName$.next(depositHolderName);
				}
			});
	}

	onDepositHolderChange(
		event: DropdownResult<{ name: string; value: DepositHolder }>,
	) {
		const depositHolder = event.result.value;
		const depositHolderName = event.result.name;

		if (this.mode === DepositHolderDropdownMode.SELF_UPDATING) {
			this.matterDispatch.SetDepositHolder(this.matterId, depositHolder);
			return;
		}

		if (this.mode === DepositHolderDropdownMode.PASSTHROUGH_SELECTION) {
			this.depositHolderName$.next(depositHolderName);
			this.onDepositHolderSelect.emit(depositHolder);
			return;
		}
	}

	resetDropdownSelection() {
		this.depositHolder$
			.pipe(untilDestroyed(this), take(1))
			.subscribe((depositHolder) => {
				if (!depositHolder) return;

				const depositHolderName = this.depositHolderSelections
					.at(0)
					.items.find((selection) => selection.value === depositHolder).name;

				this.depositHolderName$.next(depositHolderName);
			});
	}

	private getDepositHolderSelections(
		propertyStatus: PropertyStatus,
	): SelectionGroup<{ name: string; value: DepositHolder }>[] {
		const depositOptions = structuredClone(DepositHolderSelections);
		if (propertyStatus === PropertyStatus.NEW) {
			depositOptions.push({
				name: 'Builder',
				value: DepositHolder.BUILDER,
			});
		}

		return [
			{
				items: depositOptions,
			},
		];
	}
}
